/**
 * Quand une commande (boutique ou Espace Pro) disparaît — annulée par
 * changement de statut, ou SUPPRIMÉE —, son bon de livraison doit partir avec
 * elle : le stock sorti revient et le CA, assis sur les BL, cesse de compter
 * une vente qui n'aura pas lieu.
 *
 * Ce fichier ne décide QUE d'une chose : le BL peut-il partir, et sinon
 * pourquoi. La règle était jusqu'ici enfouie dans le repository et ne rendait
 * qu'un booléen — suffisant pour le rejet, qui ignore le refus et laisse le BL
 * en place, mais pas pour la suppression, qui doit DIRE à l'utilisateur ce qui
 * la bloque.
 */

export type DeliveryRemovalBlocker =
  /** Le document visé n'existe plus, ou n'est pas un BL. */
  | 'not_a_delivery'
  /** Déjà facturé (`is_invoiced`, facture groupée comprise) : la facture gouverne ce BL. */
  | 'invoiced'
  /** BL groupé : il couvre AUSSI d'autres commandes, qui gardent leur livraison. */
  | 'shared_parent'
  /** Un document enfant (facture, avoir) le référence — la FK parent_doc_id est NO ACTION. */
  | 'has_children';

export interface DeliveryRemovalInput {
  /** `doc_type` du document visé — null/absent quand la ligne n'existe plus. */
  docType?: string | null;
  isInvoiced?: boolean | null;
  parentDocId?: string | null;
  parentDocIds?: readonly (string | null)[] | null;
  /** Nombre de documents qui référencent ce BL comme parent. */
  childCount?: number | null;
  /**
   * Commande dont le retrait déclenche l'opération. Fournie, elle autorise le
   * BL à n'avoir QU'ELLE pour parent ; absente, le contrôle de parenté partagée
   * ne s'applique pas (suppression manuelle d'un BL isolé).
   */
  removedParentId?: string | null;
}

/**
 * Ce qui empêche de retirer ce BL, ou `null` s'il peut partir.
 *
 * Les trois refus disent tous la même chose sous trois formes : quelque chose
 * d'AVAL s'appuie déjà sur ce BL. Supprimer sous une facture ferait réapparaître
 * une créance sans marchandise ; supprimer sous un avoir lui ôterait sa source ;
 * supprimer un BL groupé priverait de livraison les autres commandes qu'il sert.
 */
export function deliveryRemovalBlocker(
  input: DeliveryRemovalInput,
): DeliveryRemovalBlocker | null {
  if (input.docType !== 'delivery') return 'not_a_delivery';
  if (input.isInvoiced === true) return 'invoiced';

  if (input.removedParentId) {
    const otherParents = new Set<string>();
    if (input.parentDocId) otherParents.add(input.parentDocId);
    for (const parentId of input.parentDocIds ?? []) {
      if (parentId) otherParents.add(parentId);
    }
    otherParents.delete(input.removedParentId);
    if (otherParents.size > 0) return 'shared_parent';
  }

  if ((input.childCount ?? 0) > 0) return 'has_children';

  return null;
}

/** Raccourci booléen : ce BL peut-il être retiré ? */
export function canRemoveDeliveryForRemovedOrder(input: DeliveryRemovalInput): boolean {
  return deliveryRemovalBlocker(input) === null;
}
